import React, {
    createContext,
    useContext,
    useEffect,
    useState,
    useRef,
    ReactNode,
} from "react";
import { io, Socket } from "socket.io-client";
import { getAuthHeader } from "../utils/authHeader";
import useAppDispatch from "../hooks/useAppDispatch";
import useAppSelector from "../hooks/useAppSelector";
import {
    addNotification,
    fetchNotifications,
    fetchUnreadCount,
    setNotifications,
    removeNotificationLocal,
    removeExpired,
} from "../store/notificationSlice";
import type { Notification } from "../types/notifications";

interface SocketContextProps {
    socket: Socket | null;
    isAuthenticated: boolean;
}

export const SocketContext = createContext<SocketContextProps>({
    socket: null,
    isAuthenticated: false,
});
export const useSocket = (): SocketContextProps => useContext(SocketContext);

interface SocketProviderProps {
    children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
    const dispatch = useAppDispatch();
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const socketRef = useRef<Socket | null>(null);
    const { isImpersonating, impersonationToken } = useAppSelector(
        (state) => state.auth
    );

    useEffect(() => {
        async function init() {
            try {
                // 1) initial fetch
                dispatch(fetchUnreadCount());
                dispatch(fetchNotifications({ page: 1, limit: 20 }));

                // 2) open socket
                const headers = await getAuthHeader();
                const token = headers.Authorization?.split(" ")[1];
                if (!token) throw new Error("No auth token");

                const socketAuth: Record<string, string> = { token };
                if (isImpersonating && impersonationToken) {
                    socketAuth.impersonationToken = impersonationToken;
                }

                const s = io(`${process.env.REACT_APP_BACKEND_URL}/progress`, {
                    auth: socketAuth,
                    transports: ["websocket"],
                    withCredentials: true,
                });

                s.on("connect", () => {
                    console.log("Socket connected");
                });
                s.on("authenticated", () => {
                    setIsAuthenticated(true);
                });
                s.on("disconnect", (reason) => {
                    setIsAuthenticated(false);
                    console.info("Socket disconnected:", reason);
                });
                s.on("connect_error", (err) => {
                    console.error("Socket error:", err.message);
                });

                // notifications
                s.on("notificationBatch", (list: Notification[]) => {
                    dispatch(setNotifications(list));
                });
                s.on("notification", (n: Notification) => {
                    // Add it to the list immediately so it shows without a reload, then
                    // refresh the authoritative unread badge count.
                    dispatch(addNotification(n));
                    dispatch(fetchUnreadCount());
                });
                s.on("unreadCount", () => {
                    dispatch(fetchUnreadCount());
                });

                s.on("notificationRemoved", (payload: { id: string }) => {
                    dispatch(removeNotificationLocal(payload.id));
                    dispatch(fetchUnreadCount());
                });

                // token-expiry handling
                s.on("error", async (err) => {
                    if (err === "Token expired") {
                        try {
                            const h2 = await getAuthHeader();
                            const nt = h2.Authorization?.split(" ")[1];
                            if (!nt) return console.error("No new token");
                            const newAuth: Record<string, string> = { token: nt };
                            if (isImpersonating && impersonationToken) {
                                newAuth.impersonationToken = impersonationToken;
                            }
                            s.auth = newAuth;
                            s.disconnect();
                            s.connect();
                        } catch {}
                    }
                });

                setSocket(s);
                socketRef.current = s;
            } catch (err) {
                console.error("SocketProvider init error:", err);
            }
        }

        init();
        return () => {
            socketRef.current?.disconnect();
            socketRef.current = null;
        };
    }, [dispatch, isImpersonating, impersonationToken]);

    useEffect(() => {
        const expiryInterval = setInterval(() => {
            dispatch(removeExpired());
        }, 60_000);

        return () => {
            clearInterval(expiryInterval);
        };
    }, [dispatch]);

    return (
        <SocketContext.Provider value={{ socket, isAuthenticated }}>
            {children}
        </SocketContext.Provider>
    );
};
